/**
 * Role Guard Component
 * Protects routes that require one of a given set of user profiles.
 * Must be used INSIDE an AuthGuard (assumes the user is already authenticated).
 * Redirects elsewhere (default: /dashboard) when the user lacks every allowed
 * profile — this blocks direct-URL access even when the menu entry is hidden.
 */

import { Navigate } from 'react-router-dom';

import { useAuth } from '../../store/AuthContext';
import { LoadingSpinner } from '../common/LoadingSpinner';

interface RoleGuardProps {
  children: React.ReactNode;
  /**
   * User is allowed if they have at least one of these profiles.
   * Omit to gate on `deniedStructureTypes` only.
   */
  allowedProfiles?: string[];
  /**
   * Structure types that are always denied, whatever their profiles.
   * Carriers inherit ADMIN on their own structure, so profiles alone are not
   * enough to keep them out of platform-wide pages.
   * Takes precedence over `allowedStructureTypes`.
   */
  deniedStructureTypes?: string[];
  /**
   * Structure types allowed whatever their profiles, for the pages a structure
   * type owns by nature (a carrier consults its own waybills and TAEs without
   * needing a dedicated role).
   */
  allowedStructureTypes?: string[];
  /**
   * Structure types the page is reserved to: membership is required, on top of
   * the profile check (e.g. the remittance screens exist for OGF structures only,
   * while a carrier admin also holds ADMIN on its own structure).
   */
  requiredStructureTypes?: string[];
  /**
   * User types (User.type: DRIVER, AGENT, ...) that are always denied.
   * A driver belongs to a CARRIER structure, so structure-type rules alone
   * cannot keep them out of the carrier-wide screens.
   * Takes precedence over every allow rule.
   */
  deniedUserTypes?: string[];
  /**
   * User types allowed whatever their profiles, for the pages a user type owns
   * by nature (a driver consults the waybills they are assigned to).
   */
  allowedUserTypes?: string[];
  /** Where to send users who lack access. */
  redirectTo?: string;
}

export function RoleGuard({
  children,
  allowedProfiles,
  deniedStructureTypes,
  allowedStructureTypes,
  requiredStructureTypes,
  deniedUserTypes,
  allowedUserTypes,
  redirectTo = '/dashboard',
}: RoleGuardProps) {
  const { user, isLoading } = useAuth();

  // Wait until the auth/user state is resolved before deciding.
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (deniedUserTypes && user?.user_type && deniedUserTypes.includes(user.user_type)) {
    return <Navigate to={redirectTo} replace />;
  }

  if (
    deniedStructureTypes &&
    user?.structure_type &&
    deniedStructureTypes.includes(user.structure_type)
  ) {
    return <Navigate to={redirectTo} replace />;
  }

  if (
    requiredStructureTypes &&
    !(user?.structure_type && requiredStructureTypes.includes(user.structure_type))
  ) {
    return <Navigate to={redirectTo} replace />;
  }

  // When the page is reserved to given user types, membership is required —
  // not merely a bonus like `allowedStructureTypes`.
  if (allowedUserTypes && !(user?.user_type && allowedUserTypes.includes(user.user_type))) {
    return <Navigate to={redirectTo} replace />;
  }

  const allowedByStructureType = Boolean(
    allowedStructureTypes &&
      user?.structure_type &&
      allowedStructureTypes.includes(user.structure_type),
  );

  const userProfiles = user?.profiles ?? [];
  const hasAccess =
    allowedByStructureType ||
    !allowedProfiles ||
    allowedProfiles.some((profile) => userProfiles.includes(profile));

  if (!hasAccess) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
